import React, { useLayoutEffect, useRef, useState } from 'react';
import { fmt, fmtChangeFixed, fmtCount, fmtPct } from '../../utils/formatters';
import {
  CONDITION_ORDER, CONDITION_SHORT, YearDay, aboveBelow, conditionGlyph, conditionYearSentence, dayLabel, heatStep,
  monthTotals, yearSentence, yearWeeks,
} from '../../utils/weather';
import WeatherGlyph from './WeatherGlyph';
import { FOCUS, MICRO, NOTE, TD, TD_NUM, TH, TH_NUM, TableToggle } from './weatherUi';

// The year at this shop: 52 weeks as columns, Monday on top. Each open day is
// a square colored by how it did against its usual, in five steps (two rose,
// cream, two forest); a dot marks a day with rain, snow, heat or cold. Closed
// days and holidays are blank with a dot. Pick one weather and its days stay
// while the rest fade: "the slow days were the rainy ones", or they weren't.
// On a phone it scrolls sideways inside its own card, newest weeks in view.

const STEP_FILL: Record<number, string> = {
  [-2]: 'bg-[#B54A4A]',
  [-1]: 'bg-[#B54A4A]/40',
  0: 'bg-[#FAF9F7] ring-1 ring-inset ring-[#E6E2DE]',
  1: 'bg-[#2E6B4F]/40',
  2: 'bg-[#2E6B4F]',
};
const UNMEASURED = 'bg-[#6B6670]/10';
const DAY_LABELS = ['Mon', '', 'Wed', '', 'Fri', '', 'Sun'];
const LABEL_W = 28;

type Kind = 'closed' | 'holiday' | 'unmeasured' | 'measured';
const kindOf = (d: YearDay): Kind => (!d.open ? 'closed' : d.holiday ? 'holiday' : d.x == null ? 'unmeasured' : 'measured');

/** The readout under the grid for a hovered or tapped day. */
function readout(d: YearDay): string {
  const kind = kindOf(d);
  const date = dayLabel(d.date);
  if (kind === 'closed') return `${date} · Closed`;
  const weather = d.condition ? CONDITION_SHORT[d.condition] ?? d.condition : 'Ordinary weather';
  const sold = d.actual != null ? `sold ${fmt(d.actual)}` : 'no sales';
  if (kind === 'holiday') return `${date} · Holiday · ${sold}`;
  if (kind === 'unmeasured') return `${date} · ${weather} · ${sold}, no usual to compare yet`;
  return `${date} · ${weather} · ${sold} against a usual ${fmt(d.usual ?? 0)}, ${aboveBelow(d.x as number)}`;
}

function Cell({ d, pick, onPick }: { d: YearDay | null; pick: string | null; onPick: (d: YearDay) => void }) {
  if (!d) return <div aria-hidden="true" className="aspect-square w-full" />;
  const kind = kindOf(d);
  const step = kind === 'measured' ? heatStep(d.x as number) : null;
  const fill = step != null ? STEP_FILL[step] : kind === 'unmeasured' ? UNMEASURED : '';
  const faded = pick != null && d.condition !== pick;
  return (
    <div
      data-cell={d.date} data-kind={kind} data-step={step ?? undefined} data-faded={String(faded)}
      onMouseEnter={() => onPick(d)} onClick={() => onPick(d)}
      className={`relative aspect-square w-full rounded-[3px] transition-opacity ${fill} ${faded ? 'opacity-20' : ''}`}
    >
      {(kind === 'closed' || kind === 'holiday') && (
        <span data-dot className={`absolute inset-0 m-auto h-[3px] w-[3px] rounded-full ${kind === 'holiday' ? 'bg-[#4A2D5E]' : 'bg-[#6B6670]/60'}`} />
      )}
      {d.condition && kind !== 'closed' && (
        <span
          data-condition={d.condition}
          className={`absolute inset-0 m-auto h-[5px] w-[5px] rounded-full ${step != null && Math.abs(step) === 2 ? 'bg-white' : 'bg-[#2D2A2E]'}`}
        />
      )}
    </div>
  );
}

export default function YearHeatmap({ year }: { year: YearDay[] }) {
  const scroller = useRef<HTMLDivElement>(null);
  // The first week fully in view, so a month name cut by the scroll edge is not drawn.
  const [firstWeek, setFirstWeek] = useState(0);
  const readScroll = () => {
    const el = scroller.current;
    if (!el || !el.scrollWidth) return;
    const pitch = (el.scrollWidth - LABEL_W) / 52;
    setFirstWeek(pitch > 0 ? Math.ceil(el.scrollLeft / pitch) : 0);
  };
  const [pick, setPick] = useState<string | null>(null);
  const [active, setActive] = useState<YearDay | null>(null);
  // Newest weeks in view first on a phone.
  useLayoutEffect(() => {
    const el = scroller.current;
    if (el) el.scrollLeft = el.scrollWidth;
    readScroll();
  }, []);
  const weeks = yearWeeks(year);
  if (!weeks.length) return null;
  // The same measured days the picked sentence counts (open, not a holiday, with a usual).
  const counts = CONDITION_ORDER.map((c) => ({ c, n: year.filter((d) => d.open && !d.holiday && d.x != null && d.condition === c).length })).filter((x) => x.n > 0);
  const anyUnmeasured = year.some((d) => d.open && !d.holiday && d.x == null);
  const months = monthTotals(year);
  // A month's name over the week its first day falls in.
  const monthAt = weeks.map((w, i) => {
    const first = w.find((d) => d && d.date.slice(8) === '01');
    if (first) return first.date;
    return i === 0 ? (w.find(Boolean) as YearDay | undefined)?.date ?? null : null;
  });
  const monthName = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', { month: 'short' });

  const chip = (key: string | null, label: React.ReactNode, n?: number) => (
    <button
      key={key ?? 'all'} type="button" aria-pressed={pick === key} onClick={() => setPick(key)}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-sans text-xs font-medium transition-colors ${pick === key ? 'border-[#4A2D5E]/30 bg-[#F0EBF5] text-[#4A2D5E]' : 'border-[#E6E2DE] text-[#6B6670] hover:text-[#2D2A2E]'} ${FOCUS}`}
    >
      {key && <WeatherGlyph kind={conditionGlyph(key)} size={14} />}
      {label}
      {n != null && <span className="tabular-nums text-[#6B6670]">{fmtCount(n)}</span>}
    </button>
  );

  return (
    <div data-heatmap>
      {counts.length > 0 && (
        <div role="group" aria-label="Show one weather" className="flex flex-wrap gap-1.5">
          {chip(null, 'All days')}
          {counts.map(({ c, n }) => chip(c, CONDITION_SHORT[c] ?? c, n))}
        </div>
      )}
      <p className="mt-3 min-h-[1.6em] font-sans text-sm leading-[1.6] text-[#2D2A2E]">
        {pick ? conditionYearSentence(year, pick) : 'Each square is one day against its usual. Pick a weather to see its days.'}
      </p>

      <div ref={scroller} onScroll={readScroll} className="mt-3 overflow-x-auto overscroll-x-contain pb-1" style={{ scrollbarWidth: 'thin', scrollbarColor: '#E6E2DE transparent' }}>
        {/* One grid, filled column by column: a month row, then Monday to Sunday. */}
        <div
          role="img" aria-label={yearSentence(year)}
          className="grid min-w-[46rem] grid-flow-col gap-[3px] md:min-w-0"
          style={{ gridTemplateColumns: '1.75rem repeat(52, minmax(0, 1fr))', gridTemplateRows: '1rem repeat(7, auto)' }}
        >
          {/* The weekday column stays put while the weeks scroll under it. */}
          <div aria-hidden="true" className="sticky left-0 z-10 bg-white" />
          {DAY_LABELS.map((l, i) => (
            <div key={i} data-day-label aria-hidden="true" className={`${MICRO} sticky left-0 z-10 flex items-center bg-white leading-none`}>{l}</div>
          ))}
          {weeks.map((w, i) => (
            <div key={i} data-week={i} className="contents">
              <div aria-hidden="true" className={`${MICRO} relative leading-none`}>
                {monthAt[i] && i >= firstWeek && <span className="absolute left-0 top-0 whitespace-nowrap">{monthName(monthAt[i] as string)}</span>}
              </div>
              {w.map((d, r) => <Cell key={d?.date ?? `${i}-${r}`} d={d} pick={pick} onPick={setActive} />)}
            </div>
          ))}
        </div>
      </div>

      <p data-tip aria-live="polite" className={`${NOTE} mt-2 min-h-[1.5em] tabular-nums`}>
        {active ? readout(active) : (
          <>
            {/* Cells are too small for a finger; on a phone the month table is the way in. */}
            <span className="md:hidden">Every month&rsquo;s totals are in the table below.</span>
            <span className="hidden md:inline">Hover a day for what it sold.</span>
          </>
        )}
      </p>

      <div aria-hidden="true" className={`${NOTE} mt-2 flex flex-wrap items-center gap-x-5 gap-y-2`}>
        <span className="inline-flex items-center gap-1.5 tabular-nums">
          {fmtPct(10)} below
          {[-2, -1, 0, 1, 2].map((s) => <span key={s} className={`h-2.5 w-2.5 rounded-[2px] ${STEP_FILL[s]}`} />)}
          {fmtPct(10)} above
        </span>
        <span>Middle square: within {fmtPct(3)} of usual.</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-[5px] w-[5px] rounded-full bg-[#2D2A2E]" />rain, snow, heat or cold</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-[3px] w-[3px] rounded-full bg-[#6B6670]/60" />closed</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-[3px] w-[3px] rounded-full bg-[#4A2D5E]" />holiday</span>
        {anyUnmeasured && <span className="inline-flex items-center gap-1.5"><span className={`h-2.5 w-2.5 rounded-[2px] ${UNMEASURED}`} />no usual yet</span>}
      </div>

      <TableToggle className="mt-3">
        <table className="w-full font-sans text-xs">
          <caption className="sr-only">Month by month</caption>
          <thead>
            <tr className="border-b border-[#E6E2DE]">
              <th scope="col" className={TH}>Month</th>
              <th scope="col" className={TH_NUM}>Open days</th>
              <th scope="col" className={TH_NUM}>Sold</th>
              <th scope="col" className={TH_NUM}>Against usual</th>
              <th scope="col" className={TH_NUM}>Weather days</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E6E2DE]">
            {months.map((t) => (
              <tr key={t.month}>
                <td className={TD}>{t.label}</td>
                <td className={TD_NUM}>{fmtCount(t.open)}</td>
                <td className={TD_NUM}>{fmt(t.sold)}</td>
                <td className={TD_NUM}>{t.usual > 0 ? fmtChangeFixed((t.measuredSold / t.usual - 1) * 100) : ''}</td>
                <td className={TD_NUM}>{fmtCount(t.weatherDays)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableToggle>
    </div>
  );
}
